import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Pagination,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Divider,
  IconButton,
  Badge
} from '@mui/material';
import { Search, LocalOffer, Category, Inventory, Phone, Email, Message, Close, QrCode, AddShoppingCart, ShoppingCart } from '@mui/icons-material';
import catalogService from '../services/catalogService';
import uploadService from '../services/uploadService';
import contactService from '../services/contactService';
import cartService from '../services/cartService';
import CartDrawer from '../components/CartDrawer';
import CheckoutDialog from '../components/CheckoutDialog';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../styles/common.css';

const CatalogClient = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtros
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal de detalles del producto
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  
  // Modal de consulta
  const [consultaOpen, setConsultaOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [consultaForm, setConsultaForm] = useState({
    nombre: user?.fullName || '',
    email: user?.username || '',
    telefono: '',
    mensaje: ''
  });
  const [sendingConsulta, setSendingConsulta] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Carrito de compras
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [currentCart, setCurrentCart] = useState({ items: [], total: 0 });

  // Actualizar contador del carrito
  const updateCartCount = () => {
    const count = cartService.getItemCount();
    setCartItemCount(count);
  };

  const loadInitialData = async () => {
    try {
      const [categoriesData, brandsData] = await Promise.all([
        catalogService.getCategories(),
        catalogService.getBrands()
      ]);
      
      setCategories(categoriesData.data || []);
      setBrands(brandsData.data || []);
    } catch (error) {
      console.error('Error al cargar datos iniciales:', error);
    }
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page,
        limit: 12
      };
      
      if (search) params.search = search;
      if (selectedCategory) params.categoryId = selectedCategory;
      if (selectedBrand) params.brandId = selectedBrand;

      const response = await catalogService.getProducts(params);
      
      setProducts(response.data.products || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error al cargar productos:', error);
      setError('Error al cargar el catálogo de productos');
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedCategory, selectedBrand]);

  useEffect(() => {
    loadInitialData();
    updateCartCount();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1); // Resetear a página 1 al buscar
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setPage(1);
  };

  const handleBrandChange = (e) => {
    setSelectedBrand(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price || 0);
  };


  // Manejar clic en producto - Abrir modal de detalles
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setDetailsModalOpen(true);
  };

  // Abrir modal de consulta desde el modal de detalles
  const handleOpenConsulta = () => {
    setDetailsModalOpen(false);
    setConsultaForm({
      nombre: user?.fullName || '',
      email: user?.username || '',
      telefono: '',
      mensaje: `Hola, me interesa el producto: ${selectedProduct.name}${selectedProduct.code ? ` (Código: ${selectedProduct.code})` : ''}. Me gustaría recibir más información.`
    });
    setConsultaOpen(true);
  };

  // Cerrar modal de detalles
  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
  };

  // Manejar cambios en el formulario de consulta
  const handleConsultaChange = (e) => {
    setConsultaForm({
      ...consultaForm,
      [e.target.name]: e.target.value
    });
  };

  // Enviar consulta
  const handleEnviarConsulta = async () => {
    try {
      setSendingConsulta(true);
      
      await contactService.send({
        name: consultaForm.nombre,
        email: consultaForm.email,
        phone: consultaForm.telefono,
        message: consultaForm.mensaje
      });

      setSnackbar({
        open: true,
        message: '¡Consulta enviada correctamente! Nos pondremos en contacto contigo pronto.',
        severity: 'success'
      });
      
      setConsultaOpen(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error al enviar consulta:', error);
      setSnackbar({
        open: true,
        message: 'Error al enviar la consulta. Por favor, inténtalo nuevamente.',
        severity: 'error'
      });
    } finally {
      setSendingConsulta(false);
    }
  };

  // Cerrar modal de consulta
  const handleCloseConsulta = () => {
    setConsultaOpen(false);
    setSelectedProduct(null);
  };

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Funciones del carrito
  const handleAddToCart = (product, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    cartService.addItem(product, 1);
    updateCartCount();
    
    setSnackbar({
      open: true,
      message: `${product.name} agregado al carrito`,
      severity: 'success'
    });
  };

  const handleOpenCart = () => {
    setCartOpen(true);
  };

  const handleCloseCart = () => {
    setCartOpen(false);
    updateCartCount();
  };

  const handleCheckout = (cart) => {
    setCurrentCart(cart);
    setCartOpen(false);
    setCheckoutOpen(true);
  };

  const handleCheckoutSuccess = (order) => {
    setSnackbar({
      open: true,
      message: `¡Pedido ${order.orderNumber} creado exitosamente! Un vendedor lo procesará pronto.`,
      severity: 'success'
    });
    
    updateCartCount();
    
    // Redirigir a mis pedidos después de 2 segundos
    setTimeout(() => {
      navigate('/my-orders');
    }, 2000);
  };

  return (
    <div className="page-container">
      <Container maxWidth="xl" className="p-lg">
        {/* Header */}
        <Box className="mb-lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" className="font-bold">
              Nuestra Cava
            </Typography>
            <IconButton
              color="primary"
              onClick={handleOpenCart}
              sx={{ position: 'relative' }}
            >
              <Badge badgeContent={cartItemCount} color="error">
                <ShoppingCart fontSize="large" />
              </Badge>
            </IconButton>
          </Box>
          <Box className="d-flex align-center gap-sm">
            <Chip 
              icon={<LocalOffer />} 
              label={`Usuario: ${user?.fullName}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Filtros */}
        <Box className="mb-lg">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar productos"
                variant="outlined"
                value={search}
                onChange={handleSearchChange}
                placeholder="Nombre, código o descripción..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                select
                label="Categoría"
                value={selectedCategory}
                onChange={handleCategoryChange}
                variant="outlined"
              >
                <MenuItem value="">Todas las categorías</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                select
                label="Marca"
                value={selectedBrand}
                onChange={handleBrandChange}
                variant="outlined"
              >
                <MenuItem value="">Todas las marcas</MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" className="mb-lg">
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading ? (
          <Box className="d-flex justify-center p-xl">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Grid de productos */}
            <Grid container spacing={3}>
              {products.length === 0 ? (
                <Grid item xs={12}>
                  <Box className="text-center p-xl">
                    <Inventory sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No se encontraron productos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Intenta ajustar los filtros de búsqueda
                    </Typography>
                  </Box>
                </Grid>
              ) : (
                products.map((product) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <Card 
                      className="card-hover"
                      onClick={() => handleProductClick(product)}
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: 250,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.100'
                        }}
                      >
                        <Box
                          component="img"
                          src={product.image ? uploadService.getImageUrl(product.image) : '/no-image.svg'}
                          alt={product.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.src = '/no-image.svg';
                          }}
                        />
                      </Box>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          {product.code && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              Código: {product.code}
                            </Typography>
                          )}
                          <Typography 
                            variant="h6" 
                            className="font-bold"
                            sx={{ 
                              mb: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {product.name}
                          </Typography>
                          
                          {product.category && (
                            <Chip 
                              label={product.category.name}
                              size="small"
                              sx={{ mb: 1 }}
                            />
                          )}
                          
                          {product.brand && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {product.brand.name}
                            </Typography>
                          )}
                          
                          <Box className="mt-md">
                            <Typography 
                              variant="h5" 
                              color="primary" 
                              className="font-bold"
                            >
                              {formatPrice(product.precio)}
                            </Typography>
                          </Box>

                          {product.stock !== undefined && (
                            <Box className="mt-sm">
                              <Chip
                                size="small"
                                label={product.stock > 0 ? `Stock: ${product.stock}` : 'Sin stock'}
                                color={product.stock > 0 ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </Box>
                          )}
                        </Box>

                        {/* Botón Agregar al Carrito */}
                        <Box sx={{ mt: 2 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<AddShoppingCart />}
                            onClick={(e) => handleAddToCart(product, e)}
                            disabled={!product.stock || product.stock <= 0}
                          >
                            {product.stock > 0 ? 'Agregar al Carrito' : 'Sin Stock'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>

            {/* Paginación */}
            {totalPages > 1 && (
              <Box className="d-flex justify-center mt-xl">
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* Modal de Detalles del Producto */}
        <Dialog 
          open={detailsModalOpen} 
          onClose={handleCloseDetails}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Detalles del Producto</Typography>
              <IconButton onClick={handleCloseDetails} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent dividers>
            {selectedProduct && (
              <Grid container spacing={3}>
                {/* Imagen del producto */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component="img"
                      src={selectedProduct.image ? uploadService.getImageUrl(selectedProduct.image) : '/no-image.svg'}
                      alt={selectedProduct.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.src = '/no-image.svg';
                      }}
                    />
                  </Box>
                </Grid>

                {/* Detalles del producto */}
                <Grid item xs={12} md={7}>
                  <Box>
                    <Typography variant="h5" gutterBottom className="font-bold">
                      {selectedProduct.name}
                    </Typography>

                    {/* Precio */}
                    <Typography variant="h4" color="primary.main" className="font-bold" sx={{ my: 2 }}>
                      {formatPrice(selectedProduct.precio)}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Información adicional */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {selectedProduct.code && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <QrCode fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Código:</strong> {selectedProduct.code}
                          </Typography>
                        </Box>
                      )}

                      {selectedProduct.brand && selectedProduct.brand.name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalOffer fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Marca:</strong> {selectedProduct.brand.name}
                          </Typography>
                        </Box>
                      )}

                      {selectedProduct.category && selectedProduct.category.name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Category fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            <strong>Categoría:</strong> {selectedProduct.category.name}
                          </Typography>
                        </Box>
                      )}

                      {selectedProduct.stock !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Inventory fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                            <strong>Stock:</strong>{' '}
                            {selectedProduct.stock > 0 ? (
                              <Chip 
                                label={`${selectedProduct.stock} unidades`} 
                                size="small" 
                                color="success" 
                                sx={{ ml: 1 }} 
                              />
                            ) : (
                              <Chip 
                                label="Sin stock" 
                                size="small" 
                                color="error" 
                                sx={{ ml: 1 }} 
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Descripción */}
                    {selectedProduct.description && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Typography variant="subtitle2" gutterBottom className="font-bold">
                            Descripción:
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                            {selectedProduct.description}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseDetails}>
              Cerrar
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenConsulta}
              startIcon={<Email />}
            >
              Consultar Producto
            </Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Consulta */}
        <Dialog 
          open={consultaOpen} 
          onClose={handleCloseConsulta}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box className="d-flex align-center gap-sm">
              <Message color="primary" />
              <Typography variant="h6">
                Consultar Producto
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedProduct && (
              <Box className="mb-md" sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Producto seleccionado:
                </Typography>
                <Typography variant="body1" className="font-bold">
                  {selectedProduct.name}
                </Typography>
                {selectedProduct.code && (
                  <Typography variant="caption" color="text.secondary">
                    Código: {selectedProduct.code}
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              fullWidth
              label="Nombre"
              name="nombre"
              value={consultaForm.nombre}
              onChange={handleConsultaChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalOffer />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={consultaForm.email}
              onChange={handleConsultaChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Teléfono"
              name="telefono"
              value={consultaForm.telefono}
              onChange={handleConsultaChange}
              margin="normal"
              placeholder="Ej: +54 9 11 1234-5678"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Mensaje / Consulta"
              name="mensaje"
              value={consultaForm.mensaje}
              onChange={handleConsultaChange}
              margin="normal"
              required
              multiline
              rows={4}
              placeholder="Escribe tu consulta sobre el producto..."
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseConsulta}
              disabled={sendingConsulta}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleEnviarConsulta}
              variant="contained"
              disabled={sendingConsulta || !consultaForm.nombre || !consultaForm.email || !consultaForm.mensaje}
            >
              {sendingConsulta ? <CircularProgress size={24} /> : 'Enviar Consulta'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Drawer del Carrito */}
        <CartDrawer
          open={cartOpen}
          onClose={handleCloseCart}
          onCheckout={handleCheckout}
        />

        {/* Dialog de Checkout */}
        <CheckoutDialog
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          cart={currentCart}
          onSuccess={handleCheckoutSuccess}
        />

        {/* Snackbar de notificación */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default CatalogClient;

